import { create } from "zustand";

import type { SessionTypeCategory } from "../../constants/session-types";
import type { ConcernFormValues } from "../../schemas/concern.schema";
import type { AvailableSlot, ConsultationMode, HealerSessionOption } from "../../types/booking";

export type BookingStep = 1 | 2 | 3 | 4 | 5;

interface BookingStepperState {
  step: BookingStep;
  sessionType: SessionTypeCategory | null;
  selectedMode: ConsultationMode | null;
  selectedDurationMinutes: number | null;
  selectedOption: HealerSessionOption | null;
  selectedSlot: AvailableSlot | null;
  holdId: string | null;
  holdExpiresAt: string | null;
  concern: Partial<ConcernFormValues>;
  bookingId: string | null;

  setSessionType: (sessionType: SessionTypeCategory) => void;
  setSessionOption: (option: HealerSessionOption, mode: ConsultationMode) => void;
  setSlotHold: (params: { slot: AvailableSlot; holdId: string; holdExpiresAt: string }) => void;
  setConcern: (concern: Partial<ConcernFormValues>) => void;
  setBookingId: (bookingId: string) => void;
  goToStep: (step: BookingStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  resetHold: () => void;
  reset: () => void;
}

const INITIAL_STATE: Pick<
  BookingStepperState,
  | "step"
  | "sessionType"
  | "selectedMode"
  | "selectedDurationMinutes"
  | "selectedOption"
  | "selectedSlot"
  | "holdId"
  | "holdExpiresAt"
  | "concern"
  | "bookingId"
> = {
  step: 1,
  sessionType: null,
  selectedMode: null,
  selectedDurationMinutes: null,
  selectedOption: null,
  selectedSlot: null,
  holdId: null,
  holdExpiresAt: null,
  concern: {},
  bookingId: null,
};

export const useBookingStepperStore = create<BookingStepperState>((set) => ({
  ...INITIAL_STATE,

  setSessionType: (sessionType) => set({ sessionType, step: 2 }),

  setSessionOption: (option, mode) =>
    set({
      selectedOption: option,
      selectedMode: mode,
      selectedDurationMinutes: option.durationMinutes,
      step: 3,
    }),

  setSlotHold: ({ slot, holdId, holdExpiresAt }) =>
    set({ selectedSlot: slot, holdId, holdExpiresAt, step: 4 }),

  setConcern: (concern) => set((state) => ({ concern: { ...state.concern, ...concern }, step: 5 })),

  setBookingId: (bookingId) => set({ bookingId }),

  goToStep: (step) => set({ step }),

  nextStep: () => set((state) => ({ step: Math.min(5, state.step + 1) as BookingStep })),

  previousStep: () => set((state) => ({ step: Math.max(1, state.step - 1) as BookingStep })),

  // Used when a hold expires before the seeker finishes — sends them back
  // to the slot-selection step without losing the session type/duration
  // choices already made.
  resetHold: () => set({ selectedSlot: null, holdId: null, holdExpiresAt: null, step: 3 }),

  reset: () => set({ ...INITIAL_STATE }),
}));
